import { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider } from "@/i18n";
import { AuthProvider } from "@/auth";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Stories from "@/pages/Stories";
import Story from "@/pages/Story";
import Author from "@/pages/Author";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import Contact from "@/pages/Contact";
import Donate from "@/pages/Donate";
import Bacheca from "@/pages/Bacheca";
import Dashboard from "@/pages/Dashboard";
import Sponsor from "@/pages/Sponsor";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentResult";
import Recovery from "@/pages/Recovery";
import VerifyEmail from "@/pages/VerifyEmail";
import BookOnline from "@/pages/BookOnline";
import InvoiceRequest from "@/pages/InvoiceRequest";
import AndreaChat from "@/components/AndreaChat";
import BackToTop from "@/components/BackToTop";

import api from "@/api";
import { track } from "@/telemetry";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash && !hash.includes("session_id=")) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 80);
    } else if (!hash) {
      window.scrollTo(0, 0);
    }
    track("page_view");
  }, [pathname, hash]);
  return null;
};

const Shell = () => {
  const location = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.09 });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    document.title = "Una vita da DJ — una storia d'amore · Andrea Perocchi";
    const beacon = (message, meta) => api.post("/errors", { message: String(message).slice(0, 500), url: window.location.pathname, meta }).catch(() => {});
    const onErr = (e) => beacon(e.message, "window.onerror");
    const onRej = (e) => beacon(e.reason?.message || String(e.reason), "unhandledrejection");
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      lenis.destroy();
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);

  // Google OAuth callback: fragment session_id must be processed before any route renders
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <div className="min-h-screen bg-ink text-white">
      <ScrollManager />
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/storie" element={<Stories />} />
        <Route path="/storia/:id" element={<Story />} />
        <Route path="/autore" element={<Author />} />
        <Route path="/accedi" element={<Auth />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/sponsor" element={<Sponsor />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/recupera" element={<Recovery />} />
        <Route path="/verifica-email" element={<VerifyEmail />} />
        <Route path="/libro-online" element={<BookOnline />} />
        <Route path="/fattura/:token" element={<InvoiceRequest />} />
        <Route path="/contatti" element={<Contact />} />
        <Route path="/sostieni" element={<Donate />} />
        <Route path="/bacheca" element={<Bacheca />} />
        <Route path="/pagamento/ok" element={<PaymentSuccess />} />
        <Route path="/pagamento/annullato" element={<PaymentCancel />} />
      </Routes>
      <Footer />
      <AndreaChat />
      <BackToTop />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
